import { useCallback, useContext, useEffect, useState } from 'react';
import { HttpResponse } from '../../../../../common/ajax';
import useBreakpoints from '../../../../../common/hooks/useBreakpoints';
import Carousel from '../../../../../common/widgets/Carousel';
import { CustomerInfoContext } from '../../../contexts/CustomerInfoContext';
import { DyEngagementTypes, DyPageTypeEnum } from '../../../enums';
import { parseProducts } from '../../../helpers';
import { ChooseResponseCampaignModel } from '../../../models/DynamicYield';
import { ProductCardModel, ProductListModel } from '../../../models/Product';
import { dynamicYieldService } from '../../../services/dynamic-yield';
import ProductListItem from '../ProductListItem';

interface RecommendedProductsProps {
  pageType: DyPageTypeEnum;
  categoryPageData?: {
    topLevelCategoryId: number;
    categoryId: number;
  };
  productPageData?: {
    productId: number;
  };
}

export default function RecommendedProducts(props: RecommendedProductsProps) {
  const { pageType, categoryPageData, productPageData } = props;
  const { customerInfo } = useContext(CustomerInfoContext);
  const [campaigns, setCampaigns] = useState<
    ChooseResponseCampaignModel[] | null
  >();

  const loadCampaigns = useCallback(async () => {
    const handleResponse = (
      result: HttpResponse<ChooseResponseCampaignModel[]>,
    ) => {
      if (!result.hasErrors) {
        if (result.data) {
          setCampaigns(result.data);
        }
      }
    };

    if (pageType === DyPageTypeEnum.Homepage) {
      handleResponse(await dynamicYieldService.chooseHomepage());
    }

    if (pageType === DyPageTypeEnum.Category) {
      if (!categoryPageData) {
        return;
      }
      handleResponse(
        await dynamicYieldService.chooseCategory(
          categoryPageData.topLevelCategoryId,
          categoryPageData.categoryId,
        ),
      );
    }

    if (pageType === DyPageTypeEnum.Product) {
      if (!productPageData) {
        return;
      }
      handleResponse(
        await dynamicYieldService.chooseProduct(productPageData.productId),
      );
    }

    if (pageType === DyPageTypeEnum.Cart) {
      handleResponse(await dynamicYieldService.chooseCart());
    }
  }, [categoryPageData, productPageData, pageType]);

  useEffect(() => {
    if (!customerInfo?.hasDynamicYieldConsent) {
      return;
    }
    loadCampaigns();
  }, [
    categoryPageData,
    productPageData,
    loadCampaigns,
    customerInfo?.hasDynamicYieldConsent,
  ]);

  if (!campaigns?.length) {
    return null;
  }

  return (
    <>
      {campaigns.map(c => (
        <DyCampaign
          key={c.title}
          title={c.title}
          productList={c.products}
          isHomepage={pageType === DyPageTypeEnum.Homepage}
        />
      ))}
    </>
  );
}

interface DyCampaignProps {
  title: string;
  productList: ProductListModel[];
  isHomepage: boolean;
}

const handleProductClick = (product: ProductCardModel) => {
  if (!product.dyDecisionId || !product.dySlotId) return;

  dynamicYieldService.engagement(
    DyEngagementTypes.SlotClick,
    product.dyDecisionId,
    product.dySlotId,
  );
};

const renderProductItem = (product: ProductCardModel) => (
  <ProductListItem
    key={product.productId ?? product.brandId}
    product={product}
    onProductClick={() => handleProductClick(product)}
  />
);

const getCampaignCarouselClassName = (
  isHomepage: boolean,
  isOnlyDesktop: boolean,
  productCount: number,
) =>
  [
    'edn-carousel edn-carousel-dots no-dots-mobile',
    isHomepage ? 'no-dots-tablet' : '',
    'carousel-gap-30 carousel-gap-md-20 carousel-gap-xxs-10',
    isHomepage && isOnlyDesktop && productCount > 4
      ? 'home-product-carousel--with-pagination'
      : '',
  ]
    .filter(Boolean)
    .join(' ');

const getCampaignCarouselSettings = (
  isHomepage: boolean,
  productCount: number,
) => [
  {
    breakpoint: 992,
    settings: {
      dots: isHomepage ? false : productCount > 3,
      slidesToShow: 3,
      slidesToScroll: productCount <= 3 ? 1 : 3,
      swipe: productCount > 3,
      draggable: productCount > 3,
      touchMove: productCount > 3,
    },
  },
  {
    breakpoint: 480,
    settings: {
      dots: isHomepage ? false : undefined,
      slidesToShow: 2.5,
      slidesToScroll: productCount <= 2.5 ? 1 : 2.5,
      swipe: productCount > 2.5,
      draggable: productCount > 2.5,
      touchMove: productCount > 2.5,
    },
  },
];

const DyCampaign = (props: Readonly<DyCampaignProps>) => {
  const { title, productList, isHomepage } = props;
  const { isOnlyDesktop, isOnlyTablet, isOnlyMobile } = useBreakpoints();

  const products = parseProducts(productList ?? [], false);
  const productCount = products?.length ?? 0;
  const showCardList =
    (isOnlyDesktop && productCount <= 4) ||
    (isHomepage &&
      ((isOnlyTablet && productCount <= 3) ||
        (isOnlyMobile && productCount <= 2.5)));

  if (!products?.length) {
    return null;
  }

  return (
    <section className="page-section carousel-section mb-60">
      <h2 className="section-title">
        <span>{title}</span>
      </h2>
      <div
        className={getCampaignCarouselClassName(
          isHomepage,
          isOnlyDesktop,
          productCount,
        )}
      >
        {showCardList ? (
          <div className="card-list card-list-4">
            {products.map(renderProductItem)}
          </div>
        ) : (
          <Carousel
            dots={isHomepage ? productCount > 4 : true}
            infinite={false}
            speed={500}
            slidesToShow={4}
            slidesToScroll={productCount <= 4 ? 1 : 4}
            arrows={false}
            swipe={productCount > 4}
            draggable={productCount > 4}
            touchMove={productCount > 4}
            loading={!productList}
            responsive={getCampaignCarouselSettings(isHomepage, productCount)}
          >
            {products.map(renderProductItem)}
          </Carousel>
        )}
      </div>
    </section>
  );
};
